import React, { useEffect, useState } from 'react';
import axios from 'axios';
import CallController from '../API/CallController';
import '../scss/CampaignForm.scss';

interface Product {
  id: number;
  name: string;
  price: number;
}

interface ProductWithIdDto {
  id: number;
  name: string;
  description: string;
  price: number;
  categoryId: number;
  discountedPrice?: number;
}

interface CampaignDto {
  id: number;
  title: string;
  description: string;
  startDate: string;
  endDate: string;
  products: ProductWithIdDto[];
}

interface CampaignFormProps {
  campaign: CampaignDto | null;
  onSuccess: () => void;
  onCancel: () => void;
}

interface SelectedProduct {
  productId: number;
  discountedPrice?: number;
}

const CampaignForm: React.FC<CampaignFormProps> = ({ campaign, onSuccess, onCancel }) => {
  const [title, setTitle] = useState(campaign?.title || '');
  const [description, setDescription] = useState(campaign?.description || '');
  const [startDate, setStartDate] = useState(campaign?.startDate?.slice(0, 10) || '');
  const [endDate, setEndDate] = useState(campaign?.endDate?.slice(0, 10) || '');
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<SelectedProduct[]>([]);
  const [validationError, setValidationError] = useState<string | null>(null);

  // initialize selectedProducts based on campaign products and available products
  useEffect(() => {
    if (campaign && products.length > 0) {
      const selected = campaign.products.map(p => ({
        productId: p.id,
        discountedPrice: p.discountedPrice ?? p.price
      })).filter(sp => products.some(prod => prod.id === sp.productId));

      setSelectedProducts(selected);
    } else {
      setSelectedProducts([]);
    }
  }, [campaign, products]);

  // Fetch all products when component mounts
  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const res = await axios.get(CallController.GetAllProducts);
      setProducts(res.data);
    } catch (err) {
      console.error('Kunde inte hämta produkter:', err);
    }
  };

  // Handle form submission for creating or updating a campaign
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

      const token = localStorage.getItem('token');
        if (!token) {
    alert('Du måste logga in för att kunna spara kampanjer.');
    return;
  }

        // Prepare payload with campaign data and selected products with their discounted prices
    const payload = {
      title,
      description,
      startDate,
      endDate,
      products: selectedProducts  // [{ productId, discountedPrice }] / interface
    };

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

    try {
      setValidationError(null);

      if (campaign) {
        await axios.put(CallController.UpdateCampaign(campaign.id), payload, config);
      } else {
        await axios.post(CallController.AddCampaign, payload, config);
      }

      onSuccess();
    } catch (err: any) {
      if (axios.isAxiosError(err)) {
        const message = err.response?.data || 'Ett oväntat fel inträffade.';
        setValidationError(typeof message === 'string' ? message : 'Ogiltig data.');
      } else {
        setValidationError('Något gick fel.');
      }

      console.error('Kunde inte spara kampanj:', err);
    }
  };

  // Toggle selection of a product in the campaign
  const toggleProductSelection = (productId: number) => {
    setSelectedProducts(prev => {
      const exists = prev.find(sp => sp.productId === productId);
      if (exists) {
                // Remove product if already selected
        return prev.filter(sp => sp.productId !== productId);
      } else {
                // Add product with no discounted price initially
        return [...prev, { productId }];
      }
    });
  };

  // Handle change of discounted price input for a selected product
  const handleDiscountedPriceChange = (productId: number, priceStr: string) => {
    if (priceStr === '') {
      setSelectedProducts(prev =>
        prev.map(sp =>
          sp.productId === productId ? { ...sp, discountedPrice: undefined } : sp
        )
      );
      return;
    }

    const price = parseFloat(priceStr);
    if (isNaN(price) || price < 0) return; // Ignore invalid or negative input

    // Update discountedPrice for the product
    setSelectedProducts(prev =>
      prev.map(sp =>
        sp.productId === productId ? { ...sp, discountedPrice: price } : sp
      )
    );
  };


  return (
    <form onSubmit={handleSubmit} className="campaign-form">
      <h3>{campaign ? 'Redigera kampanj' : 'Skapa ny kampanj'}</h3>

      <label>Titel</label>
      <input type="text" value={title} onChange={e => setTitle(e.target.value)} required />

      <label>Beskrivning</label>
      <textarea value={description} onChange={e => setDescription(e.target.value)} required />

      <label>Startdatum</label>
      <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} required />

      <label>Slutdatum</label>
      <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} required />

      {products.map(p => {
        const selectedProduct = selectedProducts.find(sp => sp.productId === p.id);
        const isChecked = !!selectedProduct;

        return (
          <div key={p.id} className="product-selection-row">
            <label className="product-checkbox">
              <input
                type="checkbox"
                checked={isChecked}
                onChange={() => toggleProductSelection(p.id)}
              />
              {p.name}
            </label>

            {isChecked && (
              <div className="price-inputs">
                <span className="original-price">Ord: {p.price} kr</span>
                <input
                  type="number"
                  value={selectedProduct?.discountedPrice ?? ''}
                  onChange={e => handleDiscountedPriceChange(p.id, e.target.value)}
                  placeholder="Kampanjpris"
                  className="discounted-price-input"
                  required
                />
              </div>
            )}
          </div>
        );
      })}

      {validationError && <div className="validation-error">{validationError}</div>}

      <div className="form-actions">
        <button type="submit" className="admin-button">Spara</button>
        <button type="button" className="admin-button cancel" onClick={onCancel}>Avbryt</button>
      </div>
    </form>
  );
};

export default CampaignForm;
